<template>
  <div class="teacher-app-page attendance-report-page" v-if="selectedClassRoom">
    <Toolbar />

    <v-container class="attendance-report" fluid>
      <header class="attendance-report-heading">
        <div>
          <p class="teacher-page-eyebrow">Reports</p>
          <h1>Attendance report</h1>
          <p>Review attendance patterns for any class and date range.</p>
        </div>
      </header>

      <section class="attendance-report-controls">
        <div class="attendance-report-class">
          <span><v-icon color="#2f7d4a">mdi-google-classroom</v-icon></span>
          <div>
            <label for="report-class">Class</label>
            <v-select
              id="report-class"
              v-model="selectedClassRoom"
              :items="classRooms"
              item-text="ClassRoom.name"
              return-object
              outlined
              dense
              hide-details
              :menu-props="{ offsetY: true, nudgeBottom: 6 }"
            />
          </div>
        </div>

        <div class="attendance-report-period">
          <div class="attendance-report-period__heading">
            <div><label>Reporting period</label><span>{{ rangeLabel }}</span></div>
            <v-icon size="20" color="#2f7d4a">mdi-calendar-range-outline</v-icon>
          </div>
          <div class="attendance-report-presets" role="group" aria-label="Quick date ranges">
            <button
              v-for="preset in presets"
              :key="preset.id"
              type="button"
              :class="{ active: activePreset === preset.id }"
              @click="applyPreset(preset.id)"
            >
              {{ preset.label }}
            </button>
          </div>
          <div class="attendance-report-dates">
            <label><span>From</span><input v-model="startDate" type="date" :max="endDate" @change="applyCustomRange" /></label>
            <v-icon size="17" color="#95a099">mdi-arrow-right</v-icon>
            <label><span>To</span><input v-model="endDate" type="date" :min="startDate" :max="today" @change="applyCustomRange" /></label>
          </div>
        </div>
      </section>

      <section class="attendance-report-summary" aria-label="Report summary">
        <article><span class="attendance-report-summary__icon attendance-report-summary__icon--green"><v-icon color="#2f7d4a">mdi-account-group-outline</v-icon></span><div><strong>{{ Attendance.length }}</strong><small>Students</small></div></article>
        <article><span class="attendance-report-summary__icon attendance-report-summary__icon--blue"><v-icon color="#3c6e9c">mdi-calendar-check-outline</v-icon></span><div><strong>{{ totalRecords }}</strong><small>Records</small></div></article>
        <article><span class="attendance-report-summary__icon attendance-report-summary__icon--gold"><v-icon color="#a77724">mdi-chart-donut</v-icon></span><div><strong>{{ classAverage }}%</strong><small>Class rate</small></div></article>
      </section>

      <section class="attendance-report-results">
        <div class="teacher-section-heading attendance-report-results__heading">
          <div><h2>Student attendance</h2><p>{{ selectedClassRoom.ClassRoom.name }}</p></div>
          <span>{{ rangeDayCount }} {{ rangeDayCount === 1 ? "day" : "days" }}</span>
        </div>

        <div v-if="Attendance.length" class="attendance-report-search">
          <v-text-field v-model.trim="search" outlined dense hide-details clearable prepend-inner-icon="mdi-magnify" placeholder="Search students" />
        </div>

        <div v-if="loading" class="teacher-loading-state" role="status">
          <v-progress-circular :size="40" :width="4" color="primary" indeterminate />
          <strong>Building attendance report</strong>
          <span>This may take a moment.</span>
        </div>

        <div v-else-if="filteredAttendance.length" class="attendance-report-list">
          <article v-for="item in filteredAttendance" :key="item.id || item.fullName" class="attendance-report-row">
            <div class="attendance-report-row__top">
              <span class="teacher-student-row__avatar">{{ initials(item.fullName) }}</span>
              <div><strong>{{ item.fullName }}</strong><small>{{ item.totalAttendance || 0 }} attendance records</small></div>
              <span class="attendance-report-row__rate" :class="rateClass(item.per)">{{ item.per }}%</span>
            </div>
            <v-progress-linear :value="item.per" height="5" rounded :color="rateColor(item.per)" background-color="#edf1ee" />
            <div class="attendance-report-row__stats">
              <span><i class="attendance-dot attendance-dot--present"></i><strong>{{ item.totalPresentAttendance || 0 }}</strong><small>Present</small></span>
              <span><i class="attendance-dot attendance-dot--absent"></i><strong>{{ item.totalAbsentAttendance || 0 }}</strong><small>Absent</small></span>
              <span><i class="attendance-dot attendance-dot--late"></i><strong>{{ item.totalLateAttendance || 0 }}</strong><small>Late</small></span>
              <span><i class="attendance-dot attendance-dot--leave"></i><strong>{{ item.totalLeaveAttendance || 0 }}</strong><small>Leave</small></span>
            </div>
          </article>
        </div>

        <div v-else-if="search" class="teacher-empty-state">
          <span><v-icon size="28" color="#2f7d4a">mdi-account-search-outline</v-icon></span>
          <h3>No matching students</h3>
          <p>Try another name or clear the search.</p>
        </div>

        <div v-else class="teacher-empty-state">
          <span><v-icon size="28" color="#2f7d4a">mdi-calendar-blank-outline</v-icon></span>
          <h3>No attendance data</h3>
          <p>No attendance was recorded for this class during the selected period.</p>
        </div>
      </section>
    </v-container>

    <BottomNavigation />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import attService from "@/modules/Attendance/service.js";
import Toolbar from "@/modules/Layout/components/Toolbar.vue";
import BottomNavigation from "@/modules/Layout/components/BottomNavigation.vue";

export default {
  name: "att-report",
  components: { Toolbar, BottomNavigation },
  data() {
    return {
      Attendance: [],
      loading: true,
      search: "",
      activePreset: "month",
      startDate: this.$moment().startOf("month").format("YYYY-MM-DD"),
      endDate: this.$moment().format("YYYY-MM-DD"),
      today: this.$moment().format("YYYY-MM-DD"),
      presets: [
        { id: "week", label: "This week" },
        { id: "month", label: "This month" },
        { id: "lastMonth", label: "Last month" },
      ],
    };
  },
  created() {
    document.title = "taleem - Attendance Report";
    if (!this.selectedClassRoom) this.$router.push({ path: "/" });
    else this.getAttendance();
  },
  watch: {
    selectedClassRoom(val, oldVal) {
      if (val && oldVal && val.ClassRoomId !== oldVal.ClassRoomId) this.getAttendance();
    },
  },
  computed: {
    ...mapGetters("teacherLayout", ["getClassRoom", "getTeacherStore"]),
    selectedClassRoom: {
      get() { return this.getClassRoom; },
      set(val) { this.setClassRoom(val); },
    },
    Teacher: {
      get() { return this.getTeacherStore; },
      set(val) { this.setTeacher(val); },
    },
    classRooms() {
      return this.Teacher && this.Teacher.ClassRoomTeachers ? this.Teacher.ClassRoomTeachers : [this.selectedClassRoom];
    },
    rangeLabel() {
      return `${this.$moment(this.startDate).format("MMM D, YYYY")} – ${this.$moment(this.endDate).format("MMM D, YYYY")}`;
    },
    rangeDayCount() {
      return Math.max(1, this.$moment(this.endDate).diff(this.$moment(this.startDate), "days") + 1);
    },
    filteredAttendance() {
      if (!this.search) return this.Attendance;
      const query = this.search.toLowerCase();
      return this.Attendance.filter(item => (item.fullName || "").toLowerCase().includes(query));
    },
    totalRecords() {
      return this.Attendance.reduce((total, item) => total + (Number(item.totalAttendance) || 0), 0);
    },
    classAverage() {
      if (!this.Attendance.length) return 0;
      return Math.round(this.Attendance.reduce((total, item) => total + item.per, 0) / this.Attendance.length);
    },
  },
  methods: {
    ...mapActions("teacherLayout", ["setClassRoom", "setTeacher"]),
    initials(name) {
      if (!name) return "S";
      return name.split(" ").filter(Boolean).slice(0, 2).map(part => part.charAt(0)).join("").toUpperCase();
    },
    applyPreset(id) {
      this.activePreset = id;
      const now = this.$moment();
      if (id === "week") {
        this.startDate = now.clone().startOf("isoWeek").format("YYYY-MM-DD");
        this.endDate = now.format("YYYY-MM-DD");
      } else if (id === "lastMonth") {
        const lastMonth = now.clone().subtract(1, "month");
        this.startDate = lastMonth.clone().startOf("month").format("YYYY-MM-DD");
        this.endDate = lastMonth.clone().endOf("month").format("YYYY-MM-DD");
      } else {
        this.startDate = now.clone().startOf("month").format("YYYY-MM-DD");
        this.endDate = now.format("YYYY-MM-DD");
      }
      this.getAttendance();
    },
    applyCustomRange() {
      if (!this.startDate || !this.endDate || this.$moment(this.endDate).isBefore(this.startDate)) return;
      this.activePreset = "custom";
      this.getAttendance();
    },
    rateClass(rate) {
      if (rate >= 80) return "attendance-report-row__rate--good";
      if (rate >= 60) return "attendance-report-row__rate--medium";
      return "attendance-report-row__rate--low";
    },
    rateColor(rate) {
      if (rate >= 80) return "#3d9860";
      if (rate >= 60) return "#d39a42";
      return "#cf6262";
    },
    getAttendance() {
      if (!this.selectedClassRoom || !this.startDate || !this.endDate) return;
      this.loading = true;
      this.search = "";
      const where = {
        start: this.$moment(this.startDate).startOf("day").toISOString(),
        end: this.$moment(this.endDate).endOf("day").toISOString(),
        ClassRoomId: this.selectedClassRoom.ClassRoomId,
      };
      return attService.attendanceInfo(where).then(response => {
        this.Attendance = (response.data || []).map(row => {
          const attended = (Number(row.totalPresentAttendance) || 0) + (Number(row.totalLateAttendance) || 0) + (Number(row.totalLeaveAttendance) || 0);
          const total = Number(row.totalAttendance) || 0;
          return { ...row, per: total ? Math.round((attended / total) * 100) : 0 };
        });
        return response;
      }).catch(() => {
        this.Attendance = [];
      }).finally(() => {
        this.loading = false;
      });
    },
  },
};
</script>
